// Generic email/member invite modal. Feature-agnostic chrome + validation;
// the owning feature wires its own server call via `onInvite`.
//
// `kind="email"` collects a single email and calls `onInvite(email)`.
// `kind="member"` searches the directory and lets the leader pick one member,
// calling `onInvite(userId)` with the selected member id.
//
// Originally `InviteToGroupModal` under `components/groups/`; lifted to a
// generic compound when the community-directory invite flow joined the
// shared callers (groups / circles / networks / spaces).

import {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslations } from 'use-intl';
import {
  Avatar, Body, Button, Eyebrow, Field, focusRing, Heading, Input, interactiveBg, Label, Dialog, DialogFooter, DialogSection, MOTION, Row, SearchInput, Stack, useInteractive, } from '@leanin/ui';
import { isValidEmailAddress } from '@leanin/shared/email/address';

type EmailProps = {
  readonly kind: 'email';
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly title: string;
  readonly description: string;
  // Optional copy overrides. Defaults preserve the historical group/circle
  // wording so existing callers don't need to migrate.
  readonly submitLabel?: string;
  readonly successMessage?: string;
  readonly onInvite: (email: string) => Promise<{ ok: true } | { ok: false; error: string }>;
};

export type MemberOption = {
  readonly id: string;
  readonly name: string;
  readonly subtitle?: string;
  readonly photoUrl?: string;
};

type MemberProps = {
  readonly kind: 'member';
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly title: string;
  readonly description: string;
  readonly searchMembers: (query: string) => Promise<ReadonlyArray<MemberOption>>;
  readonly onInvite: (userId: string) => Promise<{ ok: true } | { ok: false; error: string }>;
};

type Props = EmailProps | MemberProps;

export function InviteByEmailModal(props: Props) {
  if (props.kind === 'email') return <EmailInviteModal {...props} />;
  return <MemberInviteModal {...props} />;
}

function EmailInviteModal({
  open,
  onOpenChange,
  title,
  description,
  submitLabel,
  successMessage,
  onInvite,
}: EmailProps) {
  const t = useTranslations('Common');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // Set synchronously in `submit` — `submitting` render state has a window
  // before the pending render commits where an Escape would dismiss
  // mid-flight, running `reset()` and landing the invite result on a closed
  // dialog.
  const submittingRef = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const reset = () => {
    setEmail('');
    setError(null);
    setSuccess(false);
  };

  const submit = () => {
    if (submittingRef.current) return;
    const trimmed = email.trim();
    if (!isValidEmailAddress(trimmed)) {
      setError(t('widgets.invite.emailInvalid'));
      return;
    }
    submittingRef.current = true;
    setSubmitting(true);
    setError(null);
    void (async () => {
      try {
        const result = await onInvite(trimmed);
        if (result.ok) {
          setSuccess(true);
          setEmail('');
        } else {
          setError(result.error);
        }
      } finally {
        submittingRef.current = false;
        setSubmitting(false);
      }
    })();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) reset();
        onOpenChange(v);
      }}
      canDismiss={() => !submittingRef.current}
      title={title}
      description={description}
    >
      <DialogSection scrollable>
        <Stack gap="md">
          <Field label={t('widgets.invite.emailLabel')} required>
            <Input
              type="email"
              required
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setSuccess(false);
                setError(null);
              }}
              placeholder={t('widgets.invite.emailPlaceholder')}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  submit();
                }
              }}
            />
          </Field>
          {success ? (
            <Label size="sm" style={{ color: 'var(--palette-emerald)' }}>
              {successMessage ?? t('widgets.invite.inviteSent')}
            </Label>
          ) : null}
          {error !== null ? (
            <Label size="sm" color="destructive">
              {error}
            </Label>
          ) : null}
        </Stack>
      </DialogSection>
      <DialogFooter>
        <Button
          type="button"
          variant="secondary"
          onClick={() => onOpenChange(false)}
          disabled={submitting}
        >
          {t('widgets.invite.close')}
        </Button>
        <Button
          type="button"
          variant="primary"
          onClick={submit}
          loading={submitting}
        >
          {submitLabel ?? t('widgets.invite.sendInvite')}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

function MemberInviteModal({
  open,
  onOpenChange,
  title,
  description,
  searchMembers,
  onInvite,
}: MemberProps) {
  const t = useTranslations('Common');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ReadonlyArray<MemberOption>>([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState<MemberOption | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // Same role as EmailInviteModal's submittingRef: synchronous in-flight
  // latch so a dismissal can't race the pending render and wipe state
  // mid-invite.
  const submittingRef = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const reset = () => {
    setQuery('');
    setResults([]);
    setSelected(null);
    setError(null);
    setSent(false);
  };

  // Debounced typeahead — issues one search per ~250ms and ignores responses
  // that come back after the user has typed more.
  useEffect(() => {
    if (!open) return;
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setResults([]);
      setSearching(false);
      return;
    }
    let cancelled = false;
    const handle = setTimeout(() => {
      setSearching(true);
      void (async () => {
        try {
          const out = await searchMembers(trimmed);
          if (!cancelled) setResults(out);
        } finally {
          if (!cancelled) setSearching(false);
        }
      })();
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [query, open, searchMembers]);

  const submit = () => {
    if (submittingRef.current || selected === null) return;
    submittingRef.current = true;
    setSubmitting(true);
    setError(null);
    void (async () => {
      try {
        const result = await onInvite(selected.id);
        if (result.ok) {
          setSent(true);
          setSelected(null);
          setQuery('');
          setResults([]);
        } else {
          setError(result.error);
        }
      } finally {
        submittingRef.current = false;
        setSubmitting(false);
      }
    })();
  };

  const list = useMemo(() => results.slice(0, 8), [results]);

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) reset();
        onOpenChange(v);
      }}
      canDismiss={() => !submittingRef.current}
      title={title}
      description={description}
    >
      <DialogSection scrollable>
        <Stack gap="md">
          <Field label={t('widgets.invite.searchLabel')}>
            <SearchInput
              placeholder={t('widgets.invite.searchPlaceholder')}
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setSelected(null);
                setError(null);
                setSent(false);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && selected !== null) {
                  e.preventDefault();
                  submit();
                }
              }}
            />
          </Field>
          {selected !== null ? (
            <Row
              gap="sm"
              align="center"
              px="md"
              py="sm"
              style={{
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--primary)',
                background: 'var(--burgundy-tint, var(--accent))',
              }}
            >
              <Avatar src={selected.photoUrl} name={selected.name} size={32} />
              <Stack gap="xs" style={{ flex: 1, minWidth: 0 }}>
                <Heading level={5}>{selected.name}</Heading>
                {selected.subtitle ? (
                  <Body size="sm" color="mute">
                    {selected.subtitle}
                  </Body>
                ) : null}
              </Stack>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setSelected(null)}
              >
                {t('widgets.invite.clear')}
              </Button>
            </Row>
          ) : query.trim().length >= 2 ? (
            <Stack gap="none">
              {!searching && list.length === 0 ? (
                <Body
                  size="sm"
                  color="mute"
                  style={{
                    // eslint-disable-next-line @leanin/ui/no-inline-spacing-vars -- Body typography primitive; no Box prop
                    padding: 'var(--space-sm) var(--space-xs)',
                  }}
                >
                  {t('widgets.invite.noMatches')}
                </Body>
              ) : null}
              {list.map((m, i) => (
                <MemberPickItem
                  key={m.id}
                  photoUrl={m.photoUrl}
                  name={m.name}
                  subtitle={m.subtitle}
                  topBordered={i > 0}
                  onPick={() => setSelected(m)}
                />
              ))}
            </Stack>
          ) : (
            <Eyebrow tone="default">{t('widgets.invite.searchHint')}</Eyebrow>
          )}
          {sent ? (
            <Label size="sm" style={{ color: 'var(--palette-emerald)' }}>
              {t('widgets.invite.memberInviteSent')}
            </Label>
          ) : null}
          {error !== null ? (
            <Label size="sm" color="destructive">
              {error}
            </Label>
          ) : null}
        </Stack>
      </DialogSection>
      <DialogFooter>
        <Button
          type="button"
          variant="secondary"
          onClick={() => onOpenChange(false)}
          disabled={submitting}
        >
          {t('widgets.invite.close')}
        </Button>
        <Button
          type="button"
          variant="primary"
          onClick={submit}
          disabled={selected === null}
          loading={submitting}
        >
          {t('widgets.invite.sendInvite')}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

function MemberPickItem({
  photoUrl,
  name,
  subtitle,
  topBordered,
  onPick,
}: {
  photoUrl?: string;
  name: string;
  subtitle?: string;
  topBordered: boolean;
  onPick: () => void;
}) {
  const { state, bind } = useInteractive<HTMLButtonElement>({ onClick: onPick });
  return (
    <button
      type="button"
      {...bind}
      style={{
        display: 'flex',
        // eslint-disable-next-line @leanin/ui/no-inline-spacing-vars -- raw <button> member-pick row with interactive bg; no Box prop
        gap: 'var(--space-sm)',
        alignItems: 'center',
        // eslint-disable-next-line @leanin/ui/no-inline-spacing-vars -- raw <button> member-pick row; no Box prop
        padding: 'var(--space-sm) var(--space-sm)',
        background: interactiveBg(state),
        border: 'none',
        borderTop: topBordered ? '1px solid var(--border)' : 'none',
        textAlign: 'start',
        cursor: 'pointer',
        font: 'var(--type-body)',
        transition: MOTION.transition,
        ...focusRing(state.focus, { inset: true }),
      }}
    >
      <Avatar src={photoUrl} name={name} size={32} />
      <Stack gap="xs" style={{ flex: 1, minWidth: 0 }}>
        <Heading level={5}>{name}</Heading>
        {subtitle ? (
          <Body size="sm" color="mute">
            {subtitle}
          </Body>
        ) : null}
      </Stack>
    </button>
  );
}
